/**
 * Converts Supabase/PostgreSQL errors into bilingual user-facing messages.
 *
 * RETIRE-WHEN (Great Migration N-2): the PG-code branches below (23505, 23503,
 * 23514, 42501) die per caller when that caller's last direct PostgREST WRITE
 * repoints to Nest — i.e. when `tools/ci/authz-split-baseline.json` reports 0
 * for the caller's file. Delete each branch in the SAME PR that kills its last
 * producer, not in a later sweep.
 *
 * NOT deletable wholesale: `getSupabaseErrorMessage` is ALSO the shared
 * renderer for Nest domain errors — `NestRequestError.ar/en` is read here
 * first (see src/services/nestRequest.ts). That role outlives PostgREST.
 */

const COLUMN_LABELS: Record<string, { ar: string; en: string }> = {
  phone:          { ar: "رقم الجوال",              en: "Phone number" },
  id_number:      { ar: "رقم الهوية",               en: "ID number" },
  national_id:    { ar: "رقم الهوية الوطنية",       en: "National ID" },
  email:          { ar: "البريد الإلكتروني",         en: "Email" },
  cr_number:      { ar: "رقم السجل التجاري",         en: "CR number" },
  unified_number: { ar: "الرقم الموحد",              en: "Unified number" },
};

// Plain-Error messages thrown from src/lib/crossCompanyCheck.ts. These are
// JS Error instances with no Postgres `code`, so without explicit handling
// they get mis-rendered (e.g. as "no permission") at catch sites. Keep this
// list in sync with crossCompanyCheck.ts.
const CROSS_COMPANY_MESSAGES: Array<{ match: string; ar: string; en: string }> = [
  {
    match: "رقم الجوال مستخدم بالفعل في شركة أخرى",
    ar: "رقم الجوال مستخدم بالفعل في شركة أخرى",
    en: "This phone number is already registered with another company",
  },
  {
    match: "This phone number is already registered with another company",
    ar: "رقم الجوال مستخدم بالفعل في شركة أخرى",
    en: "This phone number is already registered with another company",
  },
  {
    match: "رقم الهوية مستخدم بالفعل في شركة أخرى",
    ar: "رقم الهوية مستخدم بالفعل في شركة أخرى",
    en: "This national ID is already registered with another company",
  },
  {
    match: "This national ID is already registered with another company",
    ar: "رقم الهوية مستخدم بالفعل في شركة أخرى",
    en: "This national ID is already registered with another company",
  },
  {
    match: "البريد الإلكتروني مستخدم بالفعل في شركة أخرى",
    ar: "البريد الإلكتروني مستخدم بالفعل في شركة أخرى",
    en: "This email is already registered with another company",
  },
  {
    match: "This email is already registered with another company",
    ar: "البريد الإلكتروني مستخدم بالفعل في شركة أخرى",
    en: "This email is already registered with another company",
  },
];

// Known duplicate-key column tokens, longest/most-specific first so e.g.
// "national_id" matches before a bare "id" substring would. Used to recognise
// the offending column even when it arrives wrapped in an index expression
// (canon_phone(phone), regexp_replace(id_number, …), lower(trim(email))).
const KNOWN_COLUMN_TOKENS = [
  "unified_number",
  "national_id",
  "id_number",
  "cr_number",
  "phone",
  "email",
];

/** Map a raw key/expression token onto a known column, or null if unrecognised. */
function normalizeDuplicateColumn(raw: string): string | null {
  const lower = raw.toLowerCase();
  for (const token of KNOWN_COLUMN_TOKENS) {
    if (lower.includes(token)) return token;
  }
  return null;
}

function extractDuplicateColumn(details?: string | null, message?: string | null): string | null {
  // Try details first: "Key (company_id, national_id)=(abc, 123) already exists."
  // Global indexes are expression-based, so details may read
  // "Key (canon_phone(phone))=(966…)" — normalize that to the column.
  if (details) {
    const match = details.match(/Key \(([^)]+)\)/);
    if (match) {
      const columns = match[1].split(',').map((c) => c.trim());
      const normalized = normalizeDuplicateColumn(columns[columns.length - 1]);
      if (normalized) return normalized;
      // Unrecognised details token → fall through to the constraint name below.
    }
  }
  // Fallback: parse constraint name from message, e.g.
  //   per-company (legacy): "owners_company_national_id_unique"
  //   global (current):     "tenants_phone_global_unique"
  if (message) {
    const match = message.match(/unique constraint "([^"]+)"/);
    if (match) {
      const constraint = match[1];
      // Legacy per-company naming: <table>_company_<column>_unique
      const companyMatch = constraint.match(/_company_(.+?)_unique$/);
      if (companyMatch) return companyMatch[1];
      // Current global naming: <table>_<column>_global_unique. The variable
      // table prefix (e.g. service_providers_phone_…) makes a plain regex
      // ambiguous, so match the known column suffixes directly.
      for (const col of KNOWN_COLUMN_TOKENS) {
        if (constraint.endsWith(`_${col}_global_unique`)) return col;
      }
    }
  }
  return null;
}

/**
 * Wraps an unknown error (typically a raw Supabase response object with
 * `{ code, details, hint, message }` keys) into a proper `Error` so
 * `Sentry.captureException` can serialize it usefully.
 *
 * Passing a plain object directly to Sentry produces
 *   "Object captured as exception with keys: code, details, hint, message"
 * which strips the actual error text. This helper normalizes the input
 * into an `Error` whose `.message` carries the context tag plus every
 * available Supabase field, and preserves the raw object on `.cause`
 * for runtime debugging.
 *
 * `context` should describe where the failure happened (e.g.
 * `"owner-onboarding/send-otp"`) so Sentry groups it usefully.
 */
export function toSentryError(error: unknown, context: string): Error {
  if (error instanceof Error) return error;
  if (error && typeof error === "object") {
    const e = error as { message?: unknown; code?: unknown; details?: unknown; hint?: unknown };
    const parts: string[] = [context];
    if (typeof e.code === "string" && e.code) parts.push(`code=${e.code}`);
    if (typeof e.message === "string" && e.message) parts.push(e.message);
    if (typeof e.details === "string" && e.details) parts.push(`details=${e.details}`);
    if (typeof e.hint === "string" && e.hint) parts.push(`hint=${e.hint}`);
    const wrapped = new Error(parts.join(" — "));
    (wrapped as Error & { cause?: unknown }).cause = error;
    return wrapped;
  }
  return new Error(`${context}: ${String(error)}`);
}

export function getSupabaseErrorMessage(
  error: unknown,
  fallback: { ar: string; en: string },
): { ar: string; en: string } {
  if (!error || typeof error !== "object") return fallback;

  const err = error as Record<string, unknown>;
  const code = err.code as string | undefined;
  const details = err.details as string | undefined | null;
  const errMessage = err.message as string | undefined;

  // Nest domain errors (NestRequestError / a raw `{ code, ar, en, message }`
  // body) carry a bilingual message. Prefer it — highest priority — so the UI
  // renders the server's ar/en text in the active language instead of falling
  // through to the generic `message`-only branch below. This is how the C4
  // owner-dedup 409s (owner-004/owner-005) surface their establishment/owner
  // name bilingually (#3038 AC3).
  const ar = err.ar;
  const en = err.en;
  if (typeof ar === "string" && ar.trim() && typeof en === "string" && en.trim()) {
    return { ar, en };
  }

  // Cross-company assert messages come in as plain JS Errors without a `code`.
  // Match them before any code-based branch so they don't get mis-rendered.
  if (errMessage && !code) {
    const hit = CROSS_COMPANY_MESSAGES.find((m) => errMessage === m.match);
    if (hit) return { ar: hit.ar, en: hit.en };
  }

  if (code === "23505") {
    const column = extractDuplicateColumn(details, errMessage);
    const label = column ? COLUMN_LABELS[column] : undefined;
    return label
      ? { ar: `${label.ar} مستخدم بالفعل`, en: `${label.en} already exists` }
      : { ar: "هذه البيانات مستخدمة بالفعل", en: "This record already exists" };
  }

  if (code === "23503") {
    return { ar: "بيانات مرتبطة غير موجودة", en: "A related record was not found" };
  }

  if (code === "23514") {
    return { ar: "البيانات لا تستوفي شروط التحقق", en: "Data failed a validation check" };
  }

  if (code === "42501") {
    return {
      ar: "لا تملك صلاحية تنفيذ هذا الإجراء",
      en: "You don't have permission to perform this action",
    };
  }

  if (errMessage) return { ar: errMessage, en: errMessage };

  return fallback;
}
